<template>
  <article
    :class="{
      'opacity-50': project.terminated_at
    }"
  >
    <div class="pb-2">
      <div class="flex justify-between">
        <h3 class="leading-tight mr-2">
          {{ project.ref }}
        </h3>
        <app-badge
          :icon="projectStatus.icon"
          :type="projectStatus.type"
        >
          {{ projectStatus.text }}
        </app-badge>
      </div>
    </div>
    <nav class="mb-6">
      <ul class="list-reset flex text-sm">
        <li class="mr-4">
          <a
            class="text-primary-800 hover:text-primary-500"
            :href="route('funding.projects.show', linkData)"
          >
            Overview
          </a>
        </li>
        <li class="mr-4">
          <a
            class="text-primary-800 hover:text-primary-500"
            :href="route('funding.projects.members.index', linkData)"
          >
            Members
          </a>
        </li>
        <li class="mr-4">
          <a
            :href="route('funding.projects.request.index', linkData)"
            class="hover:text-primary-500"
            :class="
              activeChangeRequests ? 'text-neutral-800' : 'text-primary-700'
            "
          >
            Change Requests
          </a>
          <span
            v-if="activeChangeRequests"
            class="bg-neutral-200 text-neutral-900 text-2xs w-4 h-4 leading-none text-center inline-flex items-center justify-center font-bold align-text-top -mt-1"
          >
            {{ activeChangeRequests }}
          </span>
        </li>
        <li class="mr-4">
          <a
            :href="route('funding.projects.finance.index', linkData)"
            class="hover:text-primary-500"
            :class="
              activeClaimRequests ? 'text-neutral-800' : 'text-primary-700'
            "
          >
            Finances
          </a>
          <span
            v-if="activeClaimRequests"
            class="bg-neutral-200 text-neutral-900 text-2xs w-4 h-4 leading-none text-center inline-flex items-center justify-center font-bold align-text-top -mt-1"
          >
            {{ activeClaimRequests }}
          </span>
        </li>
        <li class="mr-4">
          <a
            class="text-primary-800 hover:text-primary-500"
            :href="route('funding.projects.contracts.index', linkData)"
          >
            Contracts
          </a>
        </li>
      </ul>
    </nav>
    <template v-if="project.contracts.length">
      <div class="flex flex-col mt-2">
        <ul class="list-reset flex w-full">
          <li
            class="uppercase tracking-wide text-xs font-bold text-grey-600 flex-1 pl-3 pr-6"
          >
            Company
          </li>
          <li
            v-for="budget in budgets"
            :key="budget"
            class="uppercase tracking-wide text-xs font-bold text-grey-600 w-48 px-6 text-left"
          >
            {{ budget }}
          </li>
          <li
            class="uppercase tracking-wide text-xs font-bold text-grey-600 w-32 px-6 text-center"
          >
            Contracted
          </li>
          <li
            class="uppercase tracking-wide text-xs font-bold text-grey-600 w-32 px-6 text-center"
          >
            Forecasted
          </li>
        </ul>
        <div class="w-full mt-2 card is-borderless overflow-hidden">
          <ul
            v-for="partner in partners"
            :key="partner.company_id"
            class="list-reset py-2 border-l-4 flex my-px items-center"
            :class="{
              'border-primary-500': project.company_id === partner.company_id,
              'border-primary-300': project.company_id !== partner.company_id
            }"
          >
            <li class="pl-2 pr-6 flex-1">
              <div
                class="overflow-hidden whitespace-no-wrap font-semibold"
                style="text-overflow:ellipsis"
              >
                {{ partner.company.name | sentencecase }}
              </div>
            </li>
            <li
              v-for="budget in partner.budgets"
              :key="budget.id"
              class="px-6 w-48"
            >
              {{ Number(budget.current_balance) | toCurrency }} / <small>{{ Number(budget.opening_balance) | toCurrency }}</small>
            </li>
            <li class="px-6 w-32 text-center">
              <icon
                :icon="hasSigned(partner.id) ? 'check' : 'close-circle'"
                class="w-6 h-6"
                :class="{
                  'icons-positive': hasSigned(partner.id),
                  'icons-negative': !hasSigned(partner.id)
                }"
              />
            </li>
            <li class="px-6 w-32 text-center">
              <icon
                :icon="hasInitialForecast(partner) ? 'check' : 'close-circle'"
                class="w-6 h-6"
                :class="{
                  'icons-positive': hasInitialForecast(partner),
                  'icons-negative': !hasInitialForecast(partner),
                }"
              />
            </li>
          </ul>
          <dl
            class="list-reset border-t flex text-xs px-4 items-center py-4 bg-grey-100 border-grey-200 justify-between"
          >
            <template v-if="collaborationAgreement">
              <div class="w-1/4">
                <dt class="font-bold">
                  Collaboration agreement
                </dt>
                <dd>
                  <span
                    v-if="
                      !project.collaboration_agreement_uploaded &&
                        !project.terminated_at
                    "
                    class="bg-neutral-200 text-neutral-900 text-2xs w-4 h-4 leading-none text-center inline-flex items-center justify-center font-bold align-middle mr-px"
                  >
                    !
                  </span>
                  <template v-if="project.collaboration_agreement_uploaded">
                    Uploaded [
                    <a
                      :href="
                        route('applications.files.download', {
                          application: project.uuid,
                          file: project.collaboration_agreement.id
                        }) + '?project=1'
                      "
                      download
                      target="_blank"
                    >
                      Download
                    </a>
                    ]
                  </template>
                  <template v-else>
                    {{
                      !project.terminated_at ? "Required" : "Project Terminated"
                    }}
                  </template>
                </dd>
              </div>
            </template>
            <template v-if="project.conditions">
              <div class="w-1/4">
                <dt class="font-bold">
                  Contract conditions
                </dt>
                <dd>
                  <span
                    v-if="!project.conditions_evidence_approved_at"
                    class="bg-neutral-200 text-neutral-900 text-2xs w-4 h-4 leading-none text-center inline-flex items-center justify-center font-bold align-middle mr-px"
                  >
                    !
                  </span>
                  <template v-if="project.conditions_evidence_file_id">
                    {{
                      project.conditions_evidence_approved_at
                        ? "Approved"
                        : "Approval Required"
                    }}
                    [<a
                      :href="
                        route('applications.files.download', {
                          application: project.uuid,
                          file: project.conditions_evidence_file_id
                        }) + '?project=1'
                      "
                      download
                      target="_blank"
                    >
                      Download
                    </a>
                    ]
                  </template>
                  <template v-else>
                    Required
                  </template>
                </dd>
              </div>
            </template>
            <template v-if="currentStatuses.length && !project.terminated_at">
              <div class="w-1/2">
                <dt class="font-bold">
                  {{ statusReviewPeriod }} review:
                </dt>
                <dd class="flex -mx-px">
                  <div
                    v-for="status in currentStatuses"
                    :key="status.id"
                    class="border border-grey-100 h-6 text-center flex items-center justify-center flex-1 rounded"
                    :class="{
                      'bg-positive-600': status.partner_status === 3,
                      'bg-neutral-500': status.partner_status === 2,
                      'bg-negative-600': status.partner_status === 1,
                      'bg-grey-600': !status.partner_status
                    }"
                  >
                    <icon
                      :icon="
                        status.partner_monitoring_completed
                          ? 'check'
                          : 'important'
                      "
                      class="w-4 h-4"
                      :class="{
                        'icons-positive': status.partner_status === 3,
                        'icons-neutral': status.partner_status === 2,
                        'icons-negative': status.partner_status === 1,
                        'icons-grey': !status.partner_status
                      }"
                    />
                  </div>
                </dd>
              </div>
            </template>
          </dl>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="border-dashed border-4 flex items-center justify-center p-6">
        <div class="text-center">
          <icon
            icon="important"
            class="icons-primary w-12 h-12"
          />
          <h4>Awaiting Contract Generation</h4>
          <template v-if="hasContractTemplate">
            <app-button
              :disabled="generating"
              class="mt-2"
              @click="generateContract"
            >
              {{ generating ? "Generating contracts..." : "Generate Contract" }}
            </app-button>
          </template>
          <div
            v-else
            class="text-sm p-4 italic"
          >
            No contract template has been assigned for this competition.
          </div>
        </div>
      </div>
    </template>
  </article>
</template>

<script>
import { parseISO, format } from "date-fns";
export default {
  name: "FundingProjectItem",
  filters: {
    toCurrency(value) {
      return value.toLocaleString("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    }
  },
  props: {
    competitionUuid: {
      type: String,
      required: true
    },
    hasContractTemplate: {
      type: Boolean
    },
    fundingPot: {
      type: Number,
      required: true
    },
    projectData: {
      type: Object,
      required: true
    },
    collaborationAgreement: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      project: this.projectData,
      generating: false,
      updating: false
    };
  },
  computed: {
    budgets() {
      return ['Grant', 'Delivery Partners'];
    },
    activeClaimRequests() {
      return this.project.claims.length
    },
    activeChangeRequests() {
      return this.project.active_change_requests.length;
    },
    projectInitialForecastsSubmitted() {
      return this.project.partner_entries.every(partner => partner.forecasts.length > 0);
    },
    linkData() {
      return {
        funding: this.fundingPot,
        project: this.project.uuid
      };
    },
    contracts() {
      return this.project.contracts;
    },
    partners() {
      return this.project.partner_entries;
    },
    isReviewPeriod() {
      const firstCurrentReview = this.project.current_monitoring_items[0];
      return firstCurrentReview
        ? firstCurrentReview.is_claim_review_period
        : false;
    },
    currentStatuses() {
      return this.project.current_monitoring_items;
    },
    hasCompletedReview() {
      if (!this.currentStatuses && !this.currentStatuses.length) return false;
      return (
        this.currentStatuses.filter(
          status => status.partner_monitoring_completed
        ).length === this.currentStatuses.length
      );
    },
    statusReviewPeriod() {
      if (!this.currentStatuses && !this.currentStatuses.length) return;
      return format(parseISO(this.currentStatuses[0].open_date), "MMMM yyyy");
    },
    projectStatus() {
      const {
        terminated_at,
        all_contracts_signed,
        collaboration_agreement_uploaded,
        conditions,
        conditions_evicdence_file_id,
        conditions_evidence_approved_at
      } = this.project;
      const status = {};
      if (terminated_at) {
        status.icon = "close-circle";
        status.type = "negative";
        status.text = "Terminated";
      } else if (
        all_contracts_signed !== 1
        || (conditions !== null &&
          (conditions_evidence_approved_at === null || conditions_evicdence_file_id === null))
        ||(this.collaborationAgreement && collaboration_agreement_uploaded !== 1)
        || this.activeChangeRequests.length > 0
        || this.projectInitialForecastsSubmitted
      ) {
        status.icon = "important";
        status.type = "neutral";
        status.text = "Action Required";
      } else {
        status.icon = "check";
        status.type = "positive";
        status.text = "Active";
      }

      return status;
    }
  },
  methods: {
    getFunding(partner_id) {
      const contract = this.contracts.find(
        contract => contract.competition_project_partner_id === partner_id
      );
      return contract.grant
        ? contract.grant.toLocaleString("en-GB", {
            style: "currency",
            currency: "GBP"
          })
        : "---";
    },
    hasSigned(partner_id) {
      const contract = this.contracts.find(
        contract => contract.competition_project_partner_id === partner_id
      );
      return contract?.signed_by ?? "[DELETED]";
    },
    hasInitialForecast(partner) {
      return partner.forecasts?.length == 0 ?? false
    },
    generateContract() {
      if (!this.competitionUuid) return;
      this.generating = true;
      axios
        // make request
        .post(
          `/competitions/${this.competitionUuid}/process-contracts/${
            this.project.uuid
          }`
        )
        .then(({ data }) => (this.project = Object.assign(this.project, data)))
        // eslint-disable-next-line no-console
        .catch(error => console.error(error))
        .finally(() => (this.generating = false));
    }
  }
};
</script>
